/*
 * The mockup's second and third columns of `.design`. This wrapper adds no box: its two cells sit in
 * the workspace grid (`ProfileWorkspaceContainer.module.css`), the form beside the list and the live
 * side in the next column, or under both below 1280px.
 */
.layout {
  display: contents;
}

.editor {
  grid-column: 2;
  min-inline-size: 0;
  background: var(--surface-raised);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
}

.aside {
  grid-column: 3;
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  min-inline-size: 0;
}

/* Below 1280px the mockup drops the live side under the list and the form, full width. */
@media (max-width: 1279.98px) {
  .aside {
    grid-column: 1 / -1;
  }
}

@media (max-width: 1023.98px) {
  .editor,
  .aside {
    grid-column: 1;
  }
}

/* The mockup's `.card-head` and `.tier-body`. */
.head {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-block-size: 48px;
  padding: var(--space-3) var(--space-4);
  border-block-end: 1px solid var(--border);
}

.title {
  margin: 0;
  font-size: var(--font-size-13);
  font-weight: var(--font-weight-semibold);
}

.meta {
  margin-inline-start: auto;
  color: var(--text-subtle);
  font-size: var(--font-size-12);
}

.body {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding: var(--space-4);
}

/* The mockup's `.field`, `.label` and `.two`. */
.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-inline-size: 0;
}

.label {
  font-size: var(--font-size-13);
  font-weight: var(--font-weight-medium);
}

.labelRow {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  min-block-size: 20px;
}

.unit {
  color: var(--text-subtle);
  font-size: var(--font-size-12);
}

.readOnlyValue {
  margin: 0;
  min-block-size: var(--control-height);
  display: flex;
  align-items: center;
  font-size: var(--font-size-13);
}

.two {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-3);
}

@media (max-width: 1023.98px) {
  .two {
    grid-template-columns: minmax(0, 1fr);
  }
}

.palette {
  min-inline-size: 0;
  margin: 0;
  padding: 0;
  border: 0;
}

/* Mockup v3: the state colours fold under the 2x2 grid (the mockup's `details.disc`). */
.stateTrigger {
  margin-block-start: var(--space-2);
  gap: 6px;
}

.stateRegion {
  margin-block-start: var(--space-2);
}

/* The two "show more" groups sit close together, as one block of the card. */
.disclosures {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.region {
  padding-block-start: var(--space-3);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.later {
  margin: 0;
  padding: var(--space-3);
  border: 1px dashed var(--border-control);
  border-radius: var(--radius-card);
  color: var(--text-subtle);
  font-size: var(--font-size-12);
  text-align: center;
}

.actions {
  display: flex;
  gap: var(--space-2);
}

.saved {
  margin: 0;
  font-size: var(--font-size-13);
}

.error {
  margin: var(--space-2) 0 0;
  color: var(--danger-text);
  font-size: var(--font-size-13);
}

.srOnly {
  position: absolute;
  inline-size: 1px;
  block-size: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

/* Mockup v3 P5: the live sample under "Tipografía" (the mockup's `.font-sample`); the stack is set from code. */
.fontSample {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: var(--space-3);
  border: 1px solid var(--border);
  border-radius: var(--radius-control);
  background: var(--surface-sunken);
  color: var(--text);
  font-size: var(--font-size-13);
}

.fontSample b {
  font-size: var(--font-size-16);
  font-weight: var(--font-weight-semibold);
}

.fontSample span {
  color: var(--text-muted);
}

/* Mockup v3 P5: the name renamed in place (the mockup's `.name-row`) and its one-line hint. */
.nameRow {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-inline-size: 0;
}

.nameRow > input {
  flex: 1;
  min-inline-size: 0;
}

.hint {
  color: var(--text-muted);
  font-size: var(--font-size-12);
}

/* Audit P5: the mockup v3 `.notice` of "Ver perfil" (why a read-only profile cannot be changed). */
.notice {
  display: flex;
  gap: var(--space-2);
  align-items: flex-start;
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: var(--radius-control);
  background: var(--surface-sunken);
  color: var(--text-muted);
  font-size: var(--font-size-13);
}

.notice > svg {
  flex: none;
  margin-block-start: 2px;
}

.noticeText {
  margin: 0;
}

/* A read-only profile's stored logo and reference images, shown without an uploader. */
.storedImages {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin: 0;
  padding: 0;
  list-style: none;
}

.storedImage {
  display: block;
  max-inline-size: 160px;
  max-block-size: 96px;
  padding: var(--space-2);
  border: 1px solid var(--border);
  border-radius: var(--radius-control);
  background: var(--surface-sunken);
  object-fit: contain;
}
